/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  Box,
  Button,
  Card,
  Center,
  CloseButton,
  Divider,
  Radio,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { closeAllModals, type ContextModalProps } from '@mantine/modals';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useMemo } from 'react';
import CustomImage from '@/components/custom-image';
import { ModalName, showModal } from '@/components/modals';
import PaymentSelectionItem from '@/components/modals/payment-modal/components/payment-selection-item';
import { usePaymentMethodHook } from '@/hooks';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useDetectModalStore } from '@/stores';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

const NotEnoughBalanceModal = ({ id }: ContextModalProps) => {
  const setDetectModalId = useDetectModalStore.use.setDetectModalId();
  const clearDetectModalId = useDetectModalStore.use.clearDetectModalId();

  const isMobile = useIsMobile();
  const tPayment = useTranslations('payment');

  const { validPaymentMethodList } = usePaymentMethodHook();

  const { setPaymentMethod: setSelectedPaymentMethod } = useBookingStore(
    (state) => state
  );

  const groupPaymentMethod = useMemo(
    () =>
      validPaymentMethodList.filter((item) =>
        ['cash', 'momo'].includes(item.code.toLowerCase())
      ) || [],
    [validPaymentMethodList]
  );

  const handleSelectPaymentMethod = (value: string) => {
    const method = validPaymentMethodList?.find(
      (item) => item.method_id === value
    );

    if (method) {
      setSelectedPaymentMethod(method);
      closeAllModals();
    }
  };

  // const getTrackingInfo = () => {
  //   const userAgent = navigator.userAgent;
  //   const platform = detectPlatform(userAgent);
  //   const device_id = getCookie('X-Device-Id');
  //   return { user_agent: userAgent, platform, device_id };
  // };

  useEffect(() => {
    setDetectModalId(id);
    return () => clearDetectModalId();
  }, [clearDetectModalId, id, setDetectModalId]);

  const handleTopUp = () => {
    // const queryParams = {
    //   paymentAction: 'topup',
    //   promo_code: searchParams.get('promo_code') || undefined,
    // };
    // let returnUrl = `${process.env.NEXT_PUBLIC_APP_URI}${locale === 'en' ? '/en' : ''}${pathname}?${objectToQuery(
    //   queryParams
    // )}`;
    // if (window.encodeURIComponent)
    //   returnUrl = window.encodeURIComponent(returnUrl);
    // window.open(
    //   `${process.env.NEXT_PUBLIC_APP_V1_URI}/balance?redirect_url=${returnUrl}`,
    //   '_self'
    // );

    showModal(
      ModalName.Topup,
      {},
      {
        p: 0,
        w: rem(410),
        styles: {
          body: {
            position: 'relative',
            padding: 0,
          },
        },
        fullScreen: isMobile,
        closeOnEscape: false,
      }
    );
  };

  return (
    <>
      <CloseButton
        pos="absolute"
        iconSize={rem(24)}
        variant="transparent"
        onClick={closeAllModals}
        c="neutral.6"
        top={rem(16)}
        style={
          isMobile
            ? {
                left: rem(16),
                outline: 'none',
              }
            : {
                right: rem(16),
                outline: 'none',
              }
        }
      />

      <Stack py={24} bg="white" gap={16}>
        <Center>
          <CustomImage
            loading="eager"
            src="/static/images/empty-balance.webp"
            alt="background"
            width={200}
            height={200}
          />
        </Center>

        <Box px={isMobile ? 16 : 24} w="100%">
          <Title ta="center" order={isMobile ? 3 : 5}>
            {tPayment('aha_balance.it_seems_not_enough_balance')}
          </Title>
          <Text ta="center" my={6} size="sm">
            {tPayment(
              'aha_balance.please_top_up_or_change_payment_method_to_continue_booking'
            )}
          </Text>
        </Box>

        <Button
          size="md"
          mih={rem(48)}
          mx={16}
          w={'calc(100px - 32)'}
          onClick={handleTopUp}
        >
          {tPayment('aha_balance.top_up')}
        </Button>

        <Divider
          px={isMobile ? 16 : 24}
          style={{ borderColor: 'neutral.2 !important' }}
          label={
            <Text c="neutral.6" size="sm">
              {tPayment('aha_balance.change_paymet_method_to')}
            </Text>
          }
          labelPosition="center"
        />

        <Card p={0}>
          {groupPaymentMethod && groupPaymentMethod?.length > 0 && (
            <Radio.Group
              bg="white"
              styles={{
                label: {
                  width: '100%',
                },
              }}
            >
              {groupPaymentMethod?.map((item, idx) => (
                <Fragment key={idx}>
                  <PaymentSelectionItem
                    method={item}
                    onSelect={handleSelectPaymentMethod}
                  />
                  {groupPaymentMethod?.length > idx + 1 && <Divider ml={55} />}
                </Fragment>
              ))}
            </Radio.Group>
          )}
        </Card>
      </Stack>
    </>
  );
};

export default NotEnoughBalanceModal;
